import {
  loadPropertyOptions,
  loadUnitOptions
} from "api/landlord/asyncUnitProperties";
import { useEffect, useState } from "react";
import { SectionCard } from "shared/app/LeadsTableView/components/CreateLeadModal/SectionCard";
import { useFormController } from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Lead } from "views/leads-id/types";

export const InterestedInSection = ({
  form,
  wrapperClassName,
  isTitleDisabled = false,
  cardClassName,
  queryLeadData,
  initialLabels
}: {
  form: ReturnType<typeof useFormController>;
  wrapperClassName?: string;
  isTitleDisabled?: boolean;
  cardClassName?: string;
  queryLeadData?: Lead;
  initialLabels?: { property?: string; unit?: string };
}) => {
  const { Unit, Property } = useUnitsPropertiesDictionary();

  const [unitError, setUnitError] = useState("");
  const [propertyError, setPropertyError] = useState("");

  // The async selects are controlled by id only, so the label of the selected
  // option is tracked here for rendering. It must follow the user's selection —
  // otherwise a newly-picked option keeps the original lead's label (its value
  // changes but the synthetic option label stayed static, showing e.g. "P#9").
  const [propertyLabel, setPropertyLabel] = useState<string | undefined>(
    initialLabels?.property ?? queryLeadData?.property?.name
  );
  const [unitLabel, setUnitLabel] = useState<string | undefined>(
    initialLabels?.unit ?? queryLeadData?.unit?.title
  );

  // Re-seed the labels from the lead when the modal (re)opens with new data,
  // mirroring how the parent modal resets form values on queryLeadData change.
  useEffect(() => {
    setPropertyLabel(initialLabels?.property ?? queryLeadData?.property?.name);
    setUnitLabel(initialLabels?.unit ?? queryLeadData?.unit?.title);
  }, [queryLeadData, initialLabels?.property, initialLabels?.unit]);

  return (
    <SectionCard
      title="Interested in"
      wrapperClassName={wrapperClassName}
      isTitleDisabled={isTitleDisabled}
      cardClassName={cardClassName}
    >
      <div
        className="grid gap-4 w-full max-w-[752px]"
        style={{ gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}
      >
        <div className="w-full">
          <AsyncSelect
            id="property"
            isClearable
            labelClassname="mb-1.5"
            label={Property}
            menuPosition="fixed"
            value={form.values.property_id || ""}
            searchable={true}
            // @ts-expect-error -- meh
            loadOptions={loadPropertyOptions}
            error={propertyError}
            options={
              form.values.property_id && propertyLabel
                ? [
                    {
                      label: propertyLabel,
                      value: form.values.property_id
                    }
                  ]
                : []
            }
            onChange={(e) => {
              if (!Array.isArray(e)) {
                const nextValue = e.target.value;
                form.setPropertyId(nextValue ? Number(nextValue) : undefined);
                setPropertyLabel(e.label);
                setPropertyError("");
                form.setUnitId();
                setUnitLabel(undefined);
              }
            }}
            additional={{
              page: 1
            }}
          />
        </div>
        <div className="w-full">
          <AsyncSelect
            id="unit"
            isClearable
            menuPosition="fixed"
            key={form.values.property_id}
            label={Unit}
            labelClassname="mb-1.5"
            disabled={!form.values.property_id}
            searchable={true}
            value={form.values.unit_id || ""}
            options={
              form.values.unit_id && unitLabel
                ? [
                    {
                      label: unitLabel,
                      value: form.values.unit_id
                    }
                  ]
                : []
            }
            // @ts-expect-error -- meh
            loadOptions={loadUnitOptions}
            error={unitError}
            additional={{ page: 1, propertyId: form.values.property_id }}
            onChange={(e) => {
              if (!Array.isArray(e)) {
                const nextValue = e.target.value;
                form.setUnitId(nextValue ? Number(nextValue) : undefined);
                setUnitLabel(e.label);
                setUnitError("");
              }
            }}
          />
        </div>
      </div>
    </SectionCard>
  );
};
